import {
  BiAxisSide,
  BiChartViz,
  BiSeriesType,
  BiVizSeries,
  BiVizSpec,
  BiVizType,
  biSeriesTypeOf,
  isBiChartViz,
  kBiComboChartTypes,
} from '@euler/model/bi/viz';

/** How one charted measure is drawn on a combo chart. */
export type BiSeriesLook = Pick<BiVizSeries, 'as' | 'axis'>;

/** The per-series type and axis a card's viz sets, by measure key. */
export function biSeriesLooksOf(
  viz: BiVizSpec,
): Record<string, BiSeriesLook> | undefined {
  if (!isBiChartViz(viz)) return undefined;
  const entries = viz.series
    .filter(entry => entry.as || entry.axis)
    .map(entry => [
      entry.key,
      {
        ...(entry.as ? { as: entry.as } : {}),
        ...(entry.axis ? { axis: entry.axis } : {}),
      },
    ]);
  return entries.length ? Object.fromEntries(entries) : undefined;
}

/** A chart type whose series may each pick their type and axis. */
export function biIsComboType(type: BiVizType): boolean {
  return kBiComboChartTypes.includes(type);
}

/** Whether a series may move to the right axis on this chart as it is. */
export function biCanGoRight(chart: BiChartViz, entry: BiVizSeries): boolean {
  if (chart.seriesBy) return false;
  // Bars stack on the left of a stacked bar chart.
  if (
    chart.type === BiVizType.StackedBar &&
    biSeriesTypeOf(chart, entry) === BiSeriesType.Bar
  )
    return false;
  // At least one series stays on the left.
  return chart.series.some(
    other => other.key !== entry.key && other.axis !== BiAxisSide.Right,
  );
}

/**
 * The chart's series with their looks, kept to what the server accepts:
 * only on combo types; a `seriesBy` split never goes right; a bar never
 * goes right on a stacked bar; something stays on the left.
 */
export function biComboSeries(
  chart: BiChartViz,
  looks: Record<string, BiSeriesLook> | undefined,
): BiVizSeries[] {
  if (!biIsComboType(chart.type) || !looks) return chart.series;
  const series = chart.series.map(entry => {
    const look = looks[entry.key];
    if (!look) return entry;
    const next: BiVizSeries = { ...entry, ...look };
    const bar = biSeriesTypeOf(chart, next) === BiSeriesType.Bar;
    if (
      next.axis === BiAxisSide.Right &&
      (chart.seriesBy || (bar && chart.type === BiVizType.StackedBar))
    )
      delete next.axis;
    return next;
  });
  if (series.length && series.every(entry => entry.axis === BiAxisSide.Right)) {
    const [first, ...rest] = series;
    const left: BiVizSeries = { ...first };
    delete left.axis;
    return [left, ...rest];
  }
  return series;
}
